import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import Component from './constants/Component';
import FetchData from './context/FetchData';
import MainLayout from './Layout/MainLayout';
import SeoLayout from './Layout/SeoLayout';
import './i18n/config';
import './style/App.scss';

// Redirect root path to language-specific path
function RootRedirect() {
  const savedLang = localStorage.getItem('langChange') || 'en';
  return <Navigate to={`/${savedLang}`} replace={true} />;
}

const App = () => {

  const root = createBrowserRouter([
    // Root redirect
    {
      path: '/',
      element: <RootRedirect />
    },
    
    // Legacy route redirects (redirect old routes to new language-prefixed routes)
    {
      path: '/about-us',
      element: <Navigate to="/en/about-us" replace />
    },
    {
      path: '/contactus',
      element: <Navigate to="/en/contactus" replace />
    },
    {
      path: '/service',
      element: <Navigate to="/en/service" replace />
    },
    {
      path: '/projects',
      element: <Navigate to="/en/projects" replace />
    },
    {
      path: '/jobs',
      element: <Navigate to="/en/jobs" replace />
    },
    {
      path: '/clients',
      element: <Navigate to="/en/clients" replace />
    },
    {
      path: '/blogs',
      element: <Navigate to="/en/blogs" replace />
    },
    {
      path: '/home',
      element: <Navigate to="/en" replace />
    },
    
    // English routes
    {
      path: '/en',
      element: <MainLayout />,
      children: [
        { index: true, element: <Component.Home /> },
        { path: 'home', element: <Component.Home /> },
        { path: 'about-us', element: <Component.About /> },
        {
          path: 'service',
          children: [
            { index: true, element: <Component.Services /> },
            { path: ':id', element: <Component.ServiceDetails /> }
          ]
        },
        {
          path: 'projects',
          children: [
            { index: true, element: <Component.Projects /> },
            { path: ':id', element: <Component.ProjectDetails /> }
          ]
        },
        { path: 'jobs', element: <Component.Jobs /> },
        { path: 'clients', element: <Component.Clints /> },
        { path: 'contactus', element: <Component.ContactUs /> },
        {
          path: 'blogs',
          children: [
            { index: true, element: <Component.Blogs /> },
            { path: ':id', element: <Component.BlogsDetails /> }
          ]
        },
        {
          path: 'serviceContact',
          children: [
            { path: ':image/:id', element: <Component.ContactUs /> }
          ]
        },
        // SEO service pages in English - directly under /en
        { path: 'branding', element: <Component.SeoBranding /> },
        { path: 'website-development', element: <Component.Website /> },
        { path: 'social-media-marketing', element: <Component.Marketing /> },
        { path: 'content-marketing', element: <Component.ContentMarketing /> },
        { path: 'visual-production', element: <Component.VisualProduction /> },
        { path: '*', element: <Navigate to="/en" replace /> }
      ]
    },

    // Arabic routes
    {
      path: '/ar',
      element: <MainLayout />,
      children: [
        { index: true, element: <Component.Home /> },
        { path: 'الصفحة-الرئيسية', element: <Component.Home /> },
        { path: 'نبذة-عنا', element: <Component.About /> },
        {
          path: 'خدماتنا',
          children: [
            { index: true, element: <Component.Services /> },
            { path: ':id', element: <Component.ServiceDetails /> }
          ]
        },
        {
          path: 'مشاريعنا',
          children: [
            { index: true, element: <Component.Projects /> },
            { path: ':id', element: <Component.ProjectDetails /> }
          ]
        },
        { path: 'وظائف', element: <Component.Jobs /> },
        { path: 'عملائنا', element: <Component.Clints /> },
        { path: 'اتصل-بنا', element: <Component.ContactUs /> },
        {
          path: 'المدونات',
          children: [
            { index: true, element: <Component.Blogs /> },
            { path: ':id', element: <Component.BlogsDetails /> }
          ]
        },
        {
          path: 'الاتصال-بالخدمة',
          children: [
            { path: ':image/:id', element: <Component.ContactUs /> }
          ]
        },
        // SEO service pages in Arabic - directly under /ar
        { path: 'العلامات-التجارية', element: <Component.SeoBranding /> },
        { path: 'تطوير-الموقع', element: <Component.Website /> },
        { path: 'تسويق-وسائل-الاعلام-الاجتماعية', element: <Component.Marketing /> },
        { path: 'تسويق-المحتوى', element: <Component.ContentMarketing /> },
        { path: 'الإنتاج-المرئي', element: <Component.VisualProduction /> },
        { path: '*', element: <Navigate to="/ar" replace /> }
      ]
    },
    
    // SEO routes - separate routes for SEO pages (using different paths to avoid conflicts)
    {
      path: '/en/services',
      element: <SeoLayout />,
      children: [
        { index: true, element: <Navigate to="/en/service" replace /> },
        { path: 'branding', element: <Component.SeoBranding /> },
        { path: 'website-development', element: <Component.Website /> },
        { path: 'social-media-marketing', element: <Component.Marketing /> },
        { path: 'content-marketing', element: <Component.ContentMarketing /> },
        { path: 'visual-production', element: <Component.VisualProduction /> },
        { path: '*', element: <Navigate to="/en" replace /> }
      ]
    },
    {
      path: '/ar/seo',
      element: <SeoLayout />,
      children: [
        { index: true, element: <Navigate to="/ar/خدماتنا" replace /> },
        { path: 'العلامات-التجارية', element: <Component.SeoBranding /> },
        { path: 'تطوير-الموقع', element: <Component.Website /> },
        { path: 'تسويق-وسائل-الاعلام-الاجتماعية', element: <Component.Marketing /> },
        { path: 'تسويق-المحتوى', element: <Component.ContentMarketing /> },
        { path: 'الإنتاج-المرئي', element: <Component.VisualProduction /> },
        { path: '*', element: <Navigate to="/ar" replace /> }
      ]
    }
  ])


  return (
    <HelmetProvider>
      <FetchData>
        <RouterProvider router={root} />
      </FetchData>
    </HelmetProvider>
  )
}

export default App
